import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { FieldArray } from 'redux-form';
import cn from 'classnames';
import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import Context from '../../Context';
import SectionsFields from '../SectionsFields';
import ProgressLink from '../ProgressLink';
import { getFormIdField } from '../../utils';
import { getSubGroupStudentsLabel } from 'chameleon-src/utility/utils';
import {
  getInputValue,
  getInputValueByIndividualAssignment,
} from '../../utils/inputLabel';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import Button from 'chameleon/ui-stack/ui/base/button';
import buildSubgroupStudents from 'chameleon/ui-stack/utilities/utils/buildSubgroupStudents';

import './styles.scss';

const getProgressRoute = (params, isPreApSubject) => {
  const { subject } = params;
  const assessmentsRoute = isPreApSubject ? 'assessments' : 'assignments';
  return `/${subject}/${assessmentsRoute}/progress/:assignmentId`;
};

/**
 * Classes dropdown for assign modal.
 * */
const SectionsDropdown = (props) => {
  const {
    assigneesName,
    updateAssigneesName,
    allAssigneesName,
    isAdminOrCoordinator,
    optionContainerRef = {},
  } = props;

  if (isAdminOrCoordinator) {
    return (
      <span className="h-12.2 flex items-center text-gray-500 italic text-14">
        Classes appear here
      </span>
    );
  }

  const {
    isAssign,
    isResourceForm,
    isAssessmentForm,
    isFinalExam,
    allSections,
    regularSections,
    updateSections,
    progressSections,
    section: contextSection,
    router,
    clearAssignees,
    selectAllAssignees,
    isPreApSubject,
    dispatch,
    subject,
    disabledStudents,
    classesStats,
    assignees,
    updateAssignees,
    allAssignees,
    openAssignments,
    setSelectedAssignment,
    selectedAssignment,
    fetchSingleAssignmentData,
    manageMultipleClassesFlag,
    isCompleted,
    students: studentsFormData,
    individualStudentAssignmentFlag,
    selectedSectionCurrentStudentsLength,
    studentsBySection,
    isSPP,
  } = useContext(Context);
  const [isOpened, setIsOpened] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [areAllSectionsSelected, setAreAllSectionsSelected] = useState(false); // remove not usefull
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const progressRoute = useRef(
    isAssessmentForm ? getProgressRoute(router.params, isPreApSubject) : null,
  );
  const allClasses = regularSections
    .concat(updateSections)
    .concat(progressSections);
  const isSingleClass = allClasses.length === 1;
  const isSingleProgressClass =
    isSingleClass && progressSections.indexOf(allClasses[0]) !== -1;
  const { classes: subjectClassList = [] } = subject || {};
  const { sub_group_students: selectedAssignmentSubGroupStudents } =
    selectedAssignment || {};
  let section = null;

  if (contextSection) {
    const { name: contextSectionName, id: contextSectionId } =
      contextSection || {};
    section = {
      ...contextSection,
      label: contextSectionName,
      value: contextSectionId,
    };

    if (isCompleted && !isFinalExam) {
      section.label = `${contextSectionName}${getSubGroupStudentsLabel(
        buildSubgroupStudents(
          selectedAssignmentSubGroupStudents,
          studentsBySection,
        ),
      )}`;
    }
  } else if (isSingleClass && !isFinalExam) {
    const { name: sectionName, id: sectionId } = allClasses[0] || {};
    section = {
      label: sectionName,
      value: sectionId,
    };
  }

  const toggleOpenedState = () => setIsOpened(!isOpened);

  const manageAll = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (areAllSectionsSelected) {
      clearAssignees();
    } else {
      selectAllAssignees();
    }
  };

  useEffect(() => {
    const onInputFocus = () => setIsOpened(true);

    if (inputRef && inputRef.current) {
      inputRef.current.addEventListener('focus', onInputFocus);
    }

    return () => {
      if (inputRef && inputRef.current) {
        inputRef.current.removeEventListener('focus', onInputFocus);
      }
    };
  }, []);

  useEffect(() => {
    const onDocumentClick = (event) => {
      const hasTarget =
        containerRef &&
        containerRef.current &&
        containerRef.current.contains(event.target);
      if (!hasTarget) {
        setIsOpened(false);
      }
    };

    if (isOpened) {
      document.addEventListener('click', onDocumentClick, true);
    }

    return () => {
      document.removeEventListener('click', onDocumentClick, true);
    };
  }, [isOpened]);

  useEffect(() => {
    const getInputValueParams = {
      selectedAssignment,
      studentsFormData,
      subjectClassList,
      selectedAssigneesLength: allAssignees.length,
      allAvailableAssigneesLength:
        regularSections.concat(updateSections).length,
      allAssignees,
      progressSections,
      disabledStudents,
      classesStats,
    };

    const {
      areAllSectionsSelected: newAreAllSectionsSelected,
      value: newInputValue,
    } =
      !isFinalExam && individualStudentAssignmentFlag
        ? getInputValueByIndividualAssignment(getInputValueParams)
        : getInputValue(getInputValueParams);

    setInputValue(newInputValue);
    setAreAllSectionsSelected(newAreAllSectionsSelected);
  }, [
    allAssignees.length,
    studentsFormData.length,
    progressSections.length,
    disabledStudents.length,
  ]);

  let isSingleAssignment = false;
  let options = [];

  if (openAssignments) {
    options = openAssignments
      .filter((assignee) => !assignee.closed)
      .map((assignee) => ({
        ...assignee,
        id: Number(assignee.id),
        label: assignee.section_name,
        value: assignee.section_name,
      }));
    isSingleAssignment = options.length === 1;
  }

  const manageMultipleClassesDropdown =
    !isAssign &&
    (isSingleAssignment ? (
      <div className="ft-cd-single-class-container">{options[0].label}</div>
    ) : (
      <AutocompleteSelect
        className="ft-amf-select-root"
        options={options}
        onChange={(selection) => {
          setSelectedAssignment(selection);
          fetchSingleAssignmentData && fetchSingleAssignmentData(selection.id);
        }}
        value={
          selectedAssignment
            ? selectedAssignment.label
              ? selectedAssignment
              : section
            : {}
        }
      />
    ));

  const SectionsFieldsArray = ({ allSectionsArray, ...otherProps }) => (
    <FieldArray
      name={allAssigneesName}
      component={SectionsFields}
      props={{
        isFinalExam,
        regularSections,
        updateSections,
        allSections: allSectionsArray,
        assignees,
        assigneesName,
        updateAssignees,
        updateAssigneesName,
        dispatch,
        subjectClassList,
        studentsFormData,
        individualStudentAssignmentFlag,
        allAssigneesName,
        isAssign,
        disabledStudents,
        progressSections,
        selectedSectionCurrentStudentsLength,
        classesStats,
        setIsOpened,
        optionContainerRef,
        ...otherProps,
      }}
    />
  );

  const manageMultipleClassesWithClassSwitch = () => {
    const allSectionsList = options.map((option) => ({
      id: getFormIdField(option.section_id, option.id),
      sectionId: option.section_id,
      assignmentId: option.id,
      name: option.section_name,
    }));
    const SectionsFieldsArrayProps = {
      setSelectedAssignment,
      fetchSingleAssignmentData,
      openAssignments: options,
      selectedAssignment,
      allSectionsArray: allSectionsList,
    };

    return (
      <Fragment>
        <div className="ft-cd-input-container">
          <div
            data-test-id={DATA_TEST_IDS.CLASSES_DROPDOWN_TOGGLE}
            role="button"
            tabIndex="-1"
            className="ft-cd-icon"
            onClick={toggleOpenedState}
          >
            <Icon id="selectbox_arrow_down" />
          </div>
          <input
            ref={inputRef}
            id={allAssigneesName}
            className="h-12.2 w-full border rounded-24.8 border-gray-500 text-14 text-gray-900 px-4.2"
            name={allAssigneesName}
            placeholder="Select an Assignment"
            value={isOpened ? '' : inputValue}
            autoComplete={allAssigneesName}
            readOnly
          />
        </div>
        {isOpened && (
          <div
            className={cn(
              'border border-gray-400 rounded-0.8 w-full max-h-80 shadow-sm mt-0.8 overflow-auto bg-white z-10 p-4.2',
              { absolute: isAssessmentForm && !isSPP },
            )}
          >
            {!!allSectionsList.length && (
              <Fragment>
                {SectionsFieldsArray(SectionsFieldsArrayProps)}
              </Fragment>
            )}
          </div>
        )}
      </Fragment>
    );
  };

  const manageSingleClassDropdown = (
    <AutocompleteSelect
      className="ft-amf-select-root"
      options={[section]}
      value={section || {}}
      disableDropdown
    />
  );

  const resourceAssignmentDropdown = isResourceForm
    ? individualStudentAssignmentFlag
      ? manageMultipleClassesWithClassSwitch()
      : manageMultipleClassesDropdown
    : null;
  const assessmentAssignmentDropdown = isAssessmentForm
    ? !isFinalExam &&
      manageMultipleClassesFlag &&
      !!openAssignments.length &&
      !isCompleted
      ? individualStudentAssignmentFlag
        ? manageMultipleClassesWithClassSwitch()
        : manageMultipleClassesDropdown
      : manageSingleClassDropdown
    : null;

  const assignmentDropdown = isResourceForm
    ? resourceAssignmentDropdown
    : assessmentAssignmentDropdown;

  const showSingleClassLabel =
    !individualStudentAssignmentFlag &&
    isAssign &&
    isSingleClass &&
    !isFinalExam;
  return (
    <div
      ref={containerRef}
      className={cn('ft-classes-dropdown-container', { opened: isOpened })}
    >
      {showSingleClassLabel ? (
        <div
          className="ft-cd-single-class-container"
          data-test-id="single-class-container"
        >
          {isSingleProgressClass ? (
            <ProgressLink
              sectionName={allClasses[0].name}
              progressRoute={progressRoute.current}
              assignmentId={allClasses[0].assignmentId}
              isAssessmentForm={isAssessmentForm}
            />
          ) : (
            allClasses[0].name
          )}
        </div>
      ) : isAssign ? (
        <Fragment>
          <div className="ft-cd-input-container">
            <div
              data-test-id={DATA_TEST_IDS.CLASSES_DROPDOWN_TOGGLE}
              role="button"
              tabIndex="-1"
              className="ft-cd-icon"
              onClick={toggleOpenedState}
            >
              <Icon id="selectbox_arrow_down" />
            </div>
            <input
              data-test-id={DATA_TEST_IDS.INPUT_DEFAULT_VALUE}
              ref={inputRef}
              id={allAssigneesName}
              className="h-12.2 w-full border rounded-24.8 border-gray-500 text-14 text-gray-900 px-4.2 py-0.8"
              name={allAssigneesName}
              placeholder="Select Classes or Students"
              value={isOpened ? '' : inputValue}
              autoComplete={allAssigneesName}
              readOnly
            />
          </div>
          {isOpened && (
            <div
              className={cn(
                'border border-gray-400 rounded-0.8 w-full max-h-80 shadow-sm mt-0.8 overflow-auto bg-white z-10 p-4.2',
                { absolute: isAssessmentForm && !isSPP },
              )}
            >
              {!!allSections.length && (
                <Fragment>
                  {!isFinalExam && (
                    <Button
                      isSecondary
                      className="mb-4.2"
                      data-test-id={DATA_TEST_IDS.SELECT_ALL_SECTIONS}
                      aria-label={
                        areAllSectionsSelected ? 'Unselect All' : 'Select All'
                      }
                      onClick={manageAll}
                    >
                      {areAllSectionsSelected ? 'Unselect All' : 'Select All'}
                    </Button>
                  )}
                  {SectionsFieldsArray({ allSectionsArray: allSections })}
                </Fragment>
              )}
              {progressSections.length > 0 && (
                <Fragment>
                  <div className="my-4.2 font-medium">Already Assigned:</div>
                  <ul className="list-none p-0 m-0">
                    {progressSections.map((progressSection) => {
                      const {
                        id: sectionId,
                        name: sectionName,
                        assignmentId,
                        isIndividualAssignment,
                        subGroupStudents,
                      } = progressSection;

                      const isaNotation =
                        !isFinalExam &&
                        individualStudentAssignmentFlag &&
                        isIndividualAssignment
                          ? getSubGroupStudentsLabel(subGroupStudents)
                          : null;

                      return (
                        <li
                          data-test-id={DATA_TEST_IDS.PROGRESS_SECTIONS}
                          key={sectionId}
                        >
                          <ProgressLink
                            sectionName={sectionName}
                            progressRoute={progressRoute.current}
                            assignmentId={assignmentId}
                            isaNotation={isaNotation}
                            isAssessmentForm={isAssessmentForm}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </Fragment>
              )}
            </div>
          )}
        </Fragment>
      ) : (
        assignmentDropdown
      )}
    </div>
  );
};

SectionsDropdown.propTypes = {};

SectionsDropdown.defaultProps = {};

SectionsDropdown.displayName = 'SectionsDropdown';

export default SectionsDropdown;
